import axios from 'axios';
// NOTE: this legacy layer is now ADMIN-ONLY (Dashboard, DshProducts,
// DshCategories). Storefront pages read the catalog from the unauthenticated
// Store API via Api/products.js and Api/categories.js (ARCH-5).
//
// Admin catalog reads deliberately stay on wc/v3 and use the signed-in
// administrator's own token: the admin product editor needs fields the Store
// API does not expose - `purchase_note` (DshProducts.jsx reads it into the
// edit form) and a true `stock_quantity`, where the Store API only reports
// `low_stock_remaining`.

// Constants
// Backend base URL — see the note in Api/axiosInstance.js. Both files must agree;
// they are separate constants rather than one shared export (tracked as ARCH-1).
const PROJECT_URL =
    process.env.REACT_APP_PROJECT_URL || 'https://mvl.shrivastech.com/';
const API_URL = `${PROJECT_URL}wp-json/wc/v3`;
const WP_MEDIA_API_URL = `${PROJECT_URL}wp-json/wp/v2/media`;
const WP_TERMS_API_URL = `${PROJECT_URL}wp-json/wp/v2/media_folder`;
const WP_USER_API_URL = `${PROJECT_URL}wp-json/wc/v3/customers`;
const SHIPMENT_TRACKING_API_URL = `${PROJECT_URL}wp-json/wc-shipment-tracking/v3`;
const JWT_AUTH_URL = `${PROJECT_URL}wp-json/jwt-auth/v1/token`;
const LOCATION_URL = `${PROJECT_URL}wp-json/custom/v1/pincodes`
const COUPON_URL = `${PROJECT_URL}wp-json/wc/v3/coupons`

// Anonymous-browsing fallback credentials — see the note in Api/axiosInstance.js.
// No hardcoded default: nothing about the account is embedded in the source.
const STOREFRONT_USER = process.env.REACT_APP_STOREFRONT_USER;
const STOREFRONT_PASS = process.env.REACT_APP_STOREFRONT_PASS;

// Login function to fetch Bearer token
const loginAndGetToken = async () => {
    if (!STOREFRONT_USER || !STOREFRONT_PASS) {
        throw new Error(
            'Storefront credentials are not configured. Set REACT_APP_STOREFRONT_USER and ' +
            'REACT_APP_STOREFRONT_PASS in the environment and rebuild (CRA inlines these at build time).'
        );
    }

    try {
        const response = await axios.post(JWT_AUTH_URL, {
            username: STOREFRONT_USER,
            password: STOREFRONT_PASS,
        });
        const token = response.data.token;
        localStorage.setItem('auth_token', token);
        return token;
    } catch (error) {
        console.error('Login failed:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error('Failed to authenticate user');
    }
};

// Is a real user signed in? See the note in Api/axiosInstance.js — the anonymous
// fallback must never replace a real user's session, or the caller silently becomes
// the shared storefront account.
const hasUserSession = () => {
    try { return Boolean(localStorage.getItem('user_data')); } catch { return false; }
};

const clearUserSession = () => {
    try {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.setItem('isAuthenticated', 'false');
    } catch { /* storage unavailable */ }
};

// Get or refresh token
const getAuthToken = async () => {
    let token = localStorage.getItem('auth_token');
    if (token) return token;
    if (hasUserSession()) {
        clearUserSession();
        throw new Error('Your session has expired. Please sign in again.');
    }
    return await loginAndGetToken();
};

// Create Axios instance with Bearer token
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add Bearer token
api.interceptors.request.use(async (config) => {
    const token = await getAuthToken();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Response interceptor for handling 401/403 errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        if (status !== 401 && status !== 403) {
            throw error;
        }
        if (hasUserSession()) {
            if (status === 401) {
                clearUserSession();
                throw new Error('Your session has expired. Please sign in again.');
            }
            throw error;
        }
        if (error.config?._retriedAfterAuth) {
            throw error;
        }
        try {
            localStorage.removeItem('auth_token');
            const token = await loginAndGetToken();
            error.config._retriedAfterAuth = true;
            error.config.headers.Authorization = `Bearer ${token}`;
            return await axios(error.config);
        } catch (retryError) {
            console.error('Retry failed after refreshing token:', retryError);
            throw retryError;
        }
    }
);

// Generic request method
export async function request(method, url, data = {}, params = {}, headers = {}) {
    try {
        const config = {
            method: method.toLowerCase(),
            url,
            data,
            params,
            headers: {
                ...headers,
                'Content-Type': headers['Content-Type'] || 'application/json',
            },
        };
        const response = await api(config);
        return response;
    } catch (error) {
        console.error(`${method.toUpperCase()} request failed:`, {
            url,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            headers: error.response?.headers,
            config: error.config,
        });
        throw new Error(error.response?.data?.message || `${method.toUpperCase()} request failed: ${error.message}`);
    }
}

// Axios instance for media-related requests
const mediaApi = axios.create({
    baseURL: WP_MEDIA_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Axios instance for terms-related requests
const termsApi = axios.create({
    baseURL: WP_TERMS_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Apply interceptors to mediaApi and termsApi
[mediaApi, termsApi].forEach((instance) => {
    instance.interceptors.request.use(async (config) => {
        const token = await getAuthToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
    });
    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error.response?.status;
            if (status !== 401 && status !== 403) {
                throw error;
            }
            if (hasUserSession()) {
                if (status === 401) {
                    clearUserSession();
                    throw new Error('Your session has expired. Please sign in again.');
                }
                throw error;
            }
            if (error.config?._retriedAfterAuth) {
                throw error;
            }
            try {
                localStorage.removeItem('auth_token');
                const token = await loginAndGetToken();
                error.config._retriedAfterAuth = true;
                error.config.headers.Authorization = `Bearer ${token}`;
                return await axios(error.config);
            } catch (retryError) {
                throw retryError;
            }
        }
    );
});

// Get folder ID by name using Premio Folders' media_folder taxonomy
const getFolderIdByName = async (folderName) => {
    try {
        const response = await termsApi.get('', {
            params: {
                search: folderName,
                per_page: 1,
            },
        });

        const folder = response.data.find(
            (f) => f.name.toLowerCase() === folderName.toLowerCase()
        );

        if (!folder) {
            throw new Error(`Folder "${folderName}" not found`);
        }

        return folder.id;
    } catch (error) {
        console.error('Error fetching folder ID by name:', {
            folderName,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url: error.config?.url,
            headers: error.config?.headers,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch folder ID');
    }
};

// Upload an image to a Premio Folders folder
export async function uploadImageToMedia(file, folderName, altText = '', authorId = null) {
    try {
        const folderId = await getFolderIdByName(folderName);
        const formData = new FormData();
        formData.append('file', file, file.name);
        if (altText) formData.append('alt_text', altText);
        formData.append('media_folder', folderId);
        if (authorId) formData.append('author', authorId);
        console.log('Uploading to media:', formData);

        const uploadResponse = await mediaApi.post('', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return {
            id: uploadResponse.data.id,
            url: uploadResponse.data.source_url,
        };
    } catch (error) {
        console.error('Error uploading image to Premio folder:', {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            folderName,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url: error.config?.url,
            headers: error.config?.headers,
        });
        throw new Error(error.response?.data?.message || 'Failed to upload image to folder');
    }
}

// Upload prescription to a Premio Folders folder
export const uploadPrescription = async (file, authorId, folderName = 'Prescription') => {
    try {
        if (!file) {
            throw new Error('No file provided for upload');
        }
        const token = localStorage.getItem('auth_token')

        const url = WP_MEDIA_API_URL;
        const folderId = await getFolderIdByName(folderName);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('media_folder', folderId);
        formData.append('author', authorId);

        const response = await axios.post(url, formData, {
            headers: {
                Authorization: `Bearer ${token}`
            },
        });

        return {
            id: response.data.id,
            url: response.data.source_url,
            title: response.data.title.rendered,
        };
    } catch (error) {
        console.error('Error uploading prescription:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || error.message);
    }
};

// Delete a media item from a Premio Folders folder
export async function deleteMediaFromFolder(id, folderName) {
    try {
        await mediaApi.delete(`${id}?force=true`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return { success: true, message: `Media item ${id} deleted from folder "${folderName}"` };
    } catch (error) {
        console.error('Error deleting media from Premio folder:', {
            id,
            folderName,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url: error.config?.url,
            headers: error.config?.headers,
        });
        throw new Error(error.response?.data?.message || 'Failed to delete media from folder');
    }
}

// Get media files by author
export const getMediaByAuthor = async (authorId, page = 1, per_page = 10) => {
    try {
        if (!authorId) {
            throw new Error('Author ID is required');
        }

        const response = await mediaApi.get('', {
            params: {
                author: authorId,
                page,
                per_page,
            },
        });

        return {
            data: response.data,
            total: response.headers['x-wp-total'] || response.data.length,
            totalPages: response.headers['x-wp-totalpages'] || 1,
        };
    } catch (error) {
        console.error('Error fetching media by author:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch media by author');
    }
};

// Retrieve media from a Premio Folders folder
export const getMediaByFolder = async (folderName, page = 1, per_page = 10) => {
    try {
        const folderId = await getFolderIdByName(folderName);
        const mediaResponse = await mediaApi.get('', {
            params: {
                media_folder: folderId,
                page,
                per_page,
            },
        });

        const mediaData = mediaResponse.data.map((item) => ({
            id: item.id,
            url: item.source_url,
            author: item.author,
            date: item.date,
        }));

        return {
            data: mediaData,
            total: parseInt(mediaResponse.headers['x-wp-total'] || mediaData.length),
            totalPages: parseInt(mediaResponse.headers['x-wp-totalpages'] || 1),
        };
    } catch (error) {
        console.error('Error fetching media by Premio folder:', {
            folderName,
            page,
            per_page,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch media by folder');
    }
};

// Get all products by page
export async function getAllProductsByPage(page = 1, per_page = 10) {
    try {
        const response = await request('GET', `${API_URL}/products`, {}, { page, per_page })
        return {
            data: response.data,
            total: parseInt(response.headers?.['x-wp-total'] || response.data.length),
            totalPages: parseInt(response.headers?.['x-wp-totalpages'] || 1),
            headers: response.headers
        };
    } catch (error) {
        console.error('Error fetching products:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        return { data: [], total: 0, totalPages: 1 };
    }
}

// Get category id by slug
export async function getCategoryBySlug(slug) {
    try {
        const response = await request('GET', `${API_URL}/products/categories`, {}, {
            search: decodeURIComponent(slug),
            per_page: 30,
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching category by slug:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        return null;
    }
}

// Get products by category ID
export async function getProductsByCategoryId(categoryId, page = 1, perPage = 30) {
    try {
        const response = await request('GET', `${API_URL}/products`, {}, {
            category: categoryId,
            page,
            per_page: perPage,
        });

        return {
            data: response.data,
            total: parseInt(response.headers['x-wp-total'] || response.data.length),
            totalPages: parseInt(response.headers['x-wp-totalpages'] || 1),
        };
    } catch (error) {
        console.error('Error fetching products by category:', {
            categoryId,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        return { data: [], total: 0, totalPages: 1 };
    }
}

// all products by slug
export async function getProductBySlug(query) {
    try {
        const response = await request('GET', `${API_URL}/products`, {}, {
            search: decodeURIComponent(query),
            per_page: 30,
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching product by slug:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch product by slug');
    }
}

// Get all products
export async function getAllProducts() {
    try {
        const response = await request('GET', `${API_URL}/products`);
        return response.data;
    } catch (error) {
        console.error('Error fetching all products:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch all products');
    }
}

// Get single product data by ID
export async function getSingleProductData(productId) {
    try {
        const response = await request('GET', `${API_URL}/products/${productId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching single product:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch single product');
    }
}

// Register user
export async function registerUser(userInfo) {
    try {
        return await api.post(WP_USER_API_URL, userInfo);
    } catch (error) {
        console.error('Error registering user:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to register user');
    }
}

// Login user
export async function loginUser(userInfo) {
    try {
        const response = await axios.post(JWT_AUTH_URL, userInfo);
        const token = response.data.token;
        localStorage.setItem('auth_token', token);
        return response.data;
    } catch (error) {
        console.error('Error logging in user:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to login user');
    }
}

// Create an order
export async function createAnOrder(userInfo) {
    try {
        const cartItems = JSON.parse(localStorage.getItem('cart')) || [];
        if (!cartItems.length) {
            throw new Error('Cart is empty');
        }

        const lineItems = cartItems.map((item) => ({
            product_id: item.id,
            quantity: item.quantity,
        }));

        const data = {
            ...userInfo,
            line_items: lineItems,
        };

        const response = await request('POST', `${API_URL}/orders`, data);
        return response.data;
    } catch (error) {
        console.error('Error creating order:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to create order');
    }
}

// Get all orders
export async function getAllOrderData(page = 1, perPage = 10) {
    try {
        const response = await request('GET', `${API_URL}/orders`, {}, {
            page,
            per_page: perPage,
        });

        return {
            data: response.data,
            total: response.headers?.['x-wp-total'] || response.length,
            totalPages: response.headers?.['x-wp-totalpages'] || 1,
        };
    } catch (error) {
        console.error('Error fetching orders:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch orders');
    }
}

// Get user data
export async function getUserData(token) {
    try {
        const response = await axios.get(`${PROJECT_URL}wp-json/wp/v2/users/me`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching user data:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch user data');
    }
}

// Get all user data
export async function getAllUserData(page = 1, per_page = 10) {
    try {
        const response = await request('GET', WP_USER_API_URL, {}, { page, per_page });
        return {
            data: response.data,
            total: response.headers?.['x-wp-total'] || response.length,
            totalPages: response.headers?.['x-wp-totalpages'] || 1,
        };
    } catch (error) {
        console.error('Error fetching all user data:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch all user data');
    }
}

// Update customer data
export async function updateCustomerData(userId, data) {
    try {
        return await request('PUT', `${WP_USER_API_URL}/${userId}`, data);
    } catch (error) {
        console.error('Error updating customer data:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update customer data');
    }
}

// Update customer role
export async function updateCustomerRole(userId, role) {
    try {
        return await request('PUT', `${WP_USER_API_URL}/${userId}`, { roles: [role] });
    } catch (error) {
        console.error('Error updating customer role:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update customer role');
    }
}

// Delete customer by ID
export async function deleteCustomerById(customerId) {
    try {
        if (!customerId) {
            throw new Error('Customer ID is required');
        }
        return await request('DELETE', `${API_URL}/customers/${customerId}`, {}, { force: true });
    } catch (error) {
        console.error('Error deleting customer:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to delete customer');
    }
}

// Get all customers
export async function getAllCustomers() {
    try {
        const response = await request('GET', `${API_URL}/customers`);
        return {
            data: response.data,
            total: response.headers?.['x-wp-total'] || response.length,
            totalPages: response.headers?.['x-wp-totalpages'] || 1,
        };
    } catch (error) {
        console.error('Error fetching customers:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch customers');
    }
}

// Fetch orders by user ID
// The signed-in customer's own orders, via custom/v1/me/orders.
//
// Previously GET wc/v3/orders?customer={id}, which the `customer` role cannot read.
// The endpoint is scoped server-side to the bearer token's user, so the userId
// argument is ignored — kept only so MyOrder.jsx needs no change. Returns an axios
// response, since the caller reads `response.data`.
export async function getOrdersByUserId(userId) {
    const token = await getAuthToken();
    try {
        return await axios.get(`${PROJECT_URL}wp-json/custom/v1/me/orders`, {
            params: { per_page: 50 },
            headers: { Authorization: `Bearer ${token}` },
        });
    } catch (error) {
        console.error('Error fetching orders by user ID:', {
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch orders by user ID');
    }
}


// Get all orders
export async function getAllOrders() {
    try {
        const response = await request('GET', `${API_URL}/orders`);
        return {
            data: response.data,
            total: response.headers?.['x-wp-total'] || response.length,
            totalPages: response.headers?.['x-wp-totalpages'] || 1,
        };
    } catch (error) {
        console.error('Error fetching all orders:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch all orders');
    }
}

// Get single order data
export async function getSingleOrderData(orderId) {
    try {
        return await request('GET', `${API_URL}/orders/${orderId}`);
    } catch (error) {
        console.error('Error fetching single order:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch single order');
    }
}

// Delete order by ID
export async function deleteOrderById(orderId) {
    try {
        return await request('DELETE', `${API_URL}/orders/${orderId}`, {}, { force: true });
    } catch (error) {
        console.error('Error deleting order:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to delete order');
    }
}

// Get all categories
export async function getAllCategories(page = 1, per_page = 10) {
    try {
        const response = await request('GET', `${API_URL}/products/categories`, {}, { page, per_page });
        return {
            data: response.data,
            total: response.headers?.['x-wp-total'] || response.length,
            totalPages: parseInt(response.headers?.['x-wp-totalpages'] || 1),
        };
    } catch (error) {
        console.error('Error fetching categories:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        return { data: [], totalPages: 1 };
    }
}

// Get all reports.
//
// `params` added for BUG-6: the reports pages collected a date range and then
// called this with no way to pass it, so every report ignored the dates. Note
// that not every reports route honours them — `/reports/sales` takes `date_min`
// and `date_max`, while `/reports/customers/totals` has no date dimension at all.
export async function getAllRetrieveProperties(route, params = {}) {
    try {
        const response = await request('GET', `${API_URL}/reports${route || ''}`, {}, params);
        return response.data;
    } catch (error) {
        console.error('Error fetching reports:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch reports');
    }
}

// Delete product by ID
export async function deleteProductById(productId) {
    try {
        return await request('DELETE', `${API_URL}/products/${productId}`, {}, { force: true });
    } catch (error) {
        console.error('Error deleting product:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to delete product');
    }
}

// Add product to store
export async function addProductToStore(data) {
    try {
        return await request('POST', `${API_URL}/products`, data);
    } catch (error) {
        console.error('Error adding product:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to add product');
    }
}

// Update product
export async function updateProductById(productId, data) {
    try {
        return await request('PUT', `${API_URL}/products/${productId}`, data);
    } catch (error) {
        console.error('Error updating product:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update product');
    }
}

// Create a new category
export async function createCategory(categoryData) {
    try {
        const data = { name: categoryData.name };

        // Use existing image ID directly
        if (categoryData.image_id) {
            data.image = { id: categoryData.image_id };
        }

        const response = await request('POST', `${API_URL}/products/categories`, data);
        return response.data;
    } catch (error) {
        console.error('Error creating category:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to create category');
    }
}


// Update a category
export async function updateCategory(id, categoryData) {
    try {
        const data = {
            name: categoryData.name,
        };

        if (categoryData.image_id) {
            data.image = { id: categoryData.image_id };
        }

        const response = await request('PUT', `${API_URL}/products/categories/${id}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating category:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update category');
    }
}

// Delete a category
export async function deleteProductCategory(id) {
    try {
        return await request('DELETE', `${API_URL}/products/categories/${id}`, {}, { force: true });
    } catch (error) {
        console.error('Error deleting category:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to delete category');
    }
}

// Update order
export async function updateOrderById(orderId, data) {
    try {
        return await request('PUT', `${API_URL}/orders/${orderId}`, data);
    } catch (error) {
        console.error('Error updating order:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update order');
    }
}

// Track order
export const fetchTrackingInfo = async (orderId) => {
    try {
        const response = await request('GET', `${SHIPMENT_TRACKING_API_URL}/orders/${orderId}/shipment-trackings`);
        return response;
    } catch (error) {
        console.error('Error fetching tracking info:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch tracking info');
    }
};

// Get review to product
export async function getProductReview(productId) {
    try {
        return await request('GET', `${API_URL}/products/reviews`, {}, {
            product: productId,
            per_page: 100,
            page: 1,
            orderby: 'date',
            order: 'desc',
        });
    } catch (error) {
        console.error('Error fetching reviews:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch reviews');
    }
}

// Add review to product
export async function addReviewToProduct(data) {
    try {
        return await request('POST', `${API_URL}/products/reviews`, data);
    } catch (error) {
        console.error('Error adding review:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to add review');
    }
}

// Get location pincode
export async function getLocationPincode() {
    try {
        const response = await axios.get(LOCATION_URL)
        return response.data;
    } catch (error) {
        console.log(error);
    }
}

// Add location pincode
export async function addLocationPincode(data) {
    try {
        const token = await getAuthToken();
        const response = await axios.post(LOCATION_URL, data, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error adding location pincode:', error.response?.data || error.message);
        throw error;
    }
}

// Get specific pincode data by pincode number
export async function getPincodeByNumber(pincode) {
    try {
        const response = await axios.get(`${LOCATION_URL}/${pincode}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching pincode data:', error);
        throw new Error('Failed to fetch pincode data');
    }
}

// Delete location pincode
export async function deleteLocationPincode(pincode) {
    try {
        const token = await getAuthToken();
        const response = await axios.delete(`${LOCATION_URL}/${pincode}`, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting location pincode:', error.response?.data || error.message);
        throw error;
    }
}

// Get coupons 
export async function getCoupons() {
    try {
        const response = await request('GET', COUPON_URL)
        return response
    } catch (error) {   
        console.log(error)        
    }
}

// Add Coupon
export async function addCoupon(data) {
    try {
        const response = await request('POST', COUPON_URL, data)
        return response
    } catch (error) {   
        console.log(error)        
    }
}

// Delete coupon
export async function deleteCoupon(id) {
    try {   
        const response = await request('DELETE', `${COUPON_URL}/${id}`)
        return response
    } catch (error) {   
        console.log(error)        
    }
}